// client/src/components/pages/LexiStar/Replay/ReplayMap.tsx
//
// Replay map (LS-09 §5.6, §6; FR-RPL-004/005) on the shared LexiStarMap:
//  - the planned route (thin solid) and its corridor (translucent band, toggle);
//  - the actual track, thicker, coloured by trip stage (LS-09 §4 palette);
//  - deviating segments in magenta — approved dashed, unapproved solid;
//  - stops as small rings; events as neutral markers (severity colour is for
//    chips and rows only, LS-09 §4) — selecting one shows its detail;
//  - the asset at the replay clock as the shared asset marker (ST-00244 option C,
//    components/map/assetMarker): a disc in its stage colour with an upright truck or
//    loader, a nose on the heading while it moves, a plain disc while it is stationary,
//    and hollow before the first and after the last fix. "Follow" keeps it in view.
// The viewport is reported upwards so the page can load full-resolution
// points for what is on screen (FR-RPL-013).
//
// ST-00245: Follow re-aims the camera at most every FOLLOW_EVERY_MS but always
// lands on the latest position (trailing update) — a scrub that ends inside the
// throttle window no longer leaves the camera where the drag started.
// ST-00243: the map's own buttons (Corridor / Follow / Fit, Street / Satellite)
// use theme tokens that keep their text readable in light and dark.
// Converted from ReplayMap.jsx in the same change (CLAUDE.md TypeScript rule).
// ST-00272: the tenant's zones, as they were at the replay clock, under the track — with a
// Zones button and per-type chips in the legend (./ReplayZones; self-contained).
// ST-00276 (map colours & states): with `displayBands` the track is coloured by the organisation's
// display rules in force at each moment, the asset by `display` (colour, hollow, ring / pulse /
// badge around it) and the legend lists the colours, the organisation's own rules, the marks and
// the fields that cannot be shown historically (./replayDisplay).
// ST-00318 (trips in Replay): from the Trips tab's retired small player, the track's start and end
// as A / B markers, and a "Speed" button that colours the track by speed (./replaySpeed) with the
// speed bands in the legend.

import { type ReactNode, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Source, Layer, Marker, Popup } from 'react-map-gl/maplibre';
import type { LngLatBoundsLike, Map as MaplibreMap } from 'maplibre-gl';
import type { Feature, FeatureCollection, Point } from 'geojson';
import { LexiStarMap } from '../../../../types/commonComponents';
import { boundsFromLngLats as boundsFromLngLatsJs } from '../../../map/geo';
import { AssetMarkerIcon, type AssetMarkerKind } from '../../../map/assetMarker';
import { ASSET_STATUS, ASSET_STATUS_CLASSES, type AssetStatusKey } from '../../../map/assetStatus';
import { legendFromRules } from '../../../map/assetVisualRules';
import AssetInfoCard, { ASSET_INFO_POPUP_CLASS } from '../../../map/AssetInfoCard';
import {
  trackFeatures,
  deviationFeatures,
  plannedFeatures,
  eventPoints,
  stageKey,
  clockTime,
  registrationOf,
  type EventPoint,
  type ReplayData,
  type ReplayPosition,
} from './replayLogic';
import useReplayZones from './ReplayZones';
import { displayTrackFeatures, type DisplayBand, type ReplayDisplayLegend } from './replayDisplay';
import DisplayMarks, { MarkGlyph, MarkerHalo } from '../../../map/DisplayMarks';
import { isHollowDisplay, type AssetDisplay } from '../../../map/displayRules';
import { eventTypeLabel } from '../../../../utils/eventTypeLabels';
import { SPEED_BANDS, speedLine } from './replaySpeed';

const boundsFromLngLats = boundsFromLngLatsJs as unknown as (
  points: [number, number][] | null
) => LngLatBoundsLike | null;

type Bounds = [[number, number], [number, number]];
type Basemap = 'street' | 'satellite';

export type ReplayMapPosition = ReplayPosition & { stage: string | null };

export interface ReplayMapProps {
  replay: ReplayData;
  position: ReplayMapPosition | null;
  /** Which silhouette to draw for the asset (assetMarkerKind). */
  assetKind?: AssetMarkerKind;
  selectedEventId?: string;
  onSelectEvent?: (e: EventPoint) => void;
  onViewport?: (v: { zoom: number; bounds: Bounds }) => void;
  fitKey?: string;
  className?: string;
  /** ST-00276: the display over time (the organisation's rules in force at each moment). */
  displayBands?: DisplayBand[];
  /** ST-00276: the display at the replay clock. */
  display?: AssetDisplay | null;
  /** ST-00276: the legend of the rules in force, and the fields Replay cannot show. */
  displayLegend?: ReplayDisplayLegend | null;
}

const DEFAULT_VIEW = { longitude: 28.5, latitude: -26.0, zoom: 6 };
const PLANNED = '#2563eb';
const DEVIATION = '#d946ef';
// In-map marks keep literal colours: the basemaps do not follow the app theme.
const MARK_INK = '#1e293b';
const MARK_PAPER = '#ffffff';
const FOLLOW_EVERY_MS = 800;
const ASSET_ICON_PX = 44; // the marker box, nose included (ST-00244)
// The track's start and end (ST-00318): literal map colours, as the Trips player had them.
const TRACK_START = '#22c55e';
const TRACK_END = '#ef4444';

/** The A / B disc at the track's start or end (also the legend's swatch, smaller). */
function EndMark({ letter, color, px = 24 }: { letter: 'A' | 'B'; color: string; px?: number }) {
  return (
    <span
      aria-hidden="true"
      className="inline-flex items-center justify-center rounded-full font-bold text-white"
      style={{
        width: px,
        height: px,
        background: color,
        border: `2px solid ${MARK_PAPER}`,
        boxShadow: '0 1px 4px rgba(0,0,0,0.35)',
        fontSize: Math.round(px * 0.5),
      }}
    >
      {letter}
    </span>
  );
}

const fc = (features: Feature<Point>[]): FeatureCollection<Point> => ({
  type: 'FeatureCollection',
  features: features.filter(Boolean),
});

// Map buttons: readable in both themes (primary-700 flips light ↔ dark with the
// theme; text-surface is white on it in light and the dark surface in dark).
const mapButton =
  'rounded-lg shadow-lg px-3 py-1.5 text-xs font-medium backdrop-blur transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500';
const mapButtonOff = 'bg-surface/95 text-slate-700 hover:bg-surface hover:text-slate-900';
const mapButtonOn = 'bg-primary-700 text-surface hover:bg-primary-800';

const BASEMAPS: { id: Basemap; label: string }[] = [
  { id: 'street', label: 'Street' },
  { id: 'satellite', label: 'Satellite' },
];

export default function ReplayMap({
  replay,
  position,
  assetKind = 'truck',
  selectedEventId,
  onSelectEvent,
  onViewport,
  fitKey,
  className = 'relative z-0 h-[360px] lg:h-[460px] rounded-xl overflow-hidden border border-slate-200',
  displayBands,
  display = null,
  displayLegend = null,
}: ReplayMapProps) {
  const mapRef = useRef<MaplibreMap | null>(null);
  const [showCorridor, setShowCorridor] = useState(true);
  const [follow, setFollow] = useState(false);
  const [basemap, setBasemap] = useState<Basemap>('street');
  // ST-00318: the track by speed instead of by stage / map colours.
  const [bySpeed, setBySpeed] = useState(false);
  const speed = useMemo(() => (bySpeed ? speedLine(replay.track) : null), [bySpeed, replay.track]);
  const trackStart = replay.track[0];
  const trackEnd = replay.track.length > 1 ? replay.track[replay.track.length - 1] : undefined;

  const tracks = useMemo(() => {
    const byStage = trackFeatures(replay);
    // ST-00276: coloured by the display rules in force (by stage without them).
    return displayBands && displayBands.length
      ? { ...byStage, stages: displayTrackFeatures(replay.track, displayBands) }
      : byStage;
  }, [replay, displayBands]);
  const deviations = useMemo(() => deviationFeatures(replay), [replay]);
  const planned = useMemo(
    () => plannedFeatures(replay.planned, replay.planned_legs),
    [replay.planned, replay.planned_legs]
  );
  const events = useMemo(() => eventPoints(replay), [replay]);
  const zones = useReplayZones(replay, position?.t ?? null); // ST-00272
  const stopsFC = useMemo(
    () =>
      fc(
        [...replay.stops, ...replay.idles]
          .filter((s) => s.lat != null && s.lon != null)
          .map((s) => ({
            type: 'Feature' as const,
            properties: {},
            geometry: { type: 'Point' as const, coordinates: [Number(s.lon), Number(s.lat)] },
          }))
      ),
    [replay.stops, replay.idles]
  );

  const bounds = useMemo(
    () =>
      boundsFromLngLats([
        ...replay.track.map((p): [number, number] => [p.lon, p.lat]),
        ...planned.routes.features.flatMap(
          (f) =>
            ((f.geometry as { coordinates?: [number, number][] })?.coordinates || []) as [
              number,
              number,
            ][]
        ),
      ]),
    [replay.track, planned.routes]
  );

  const report = useCallback(() => {
    const map = mapRef.current;
    if (!map || !onViewport) return;
    try {
      const b = map.getBounds();
      onViewport({
        zoom: map.getZoom(),
        bounds: [
          [b.getWest(), b.getSouth()],
          [b.getEast(), b.getNorth()],
        ],
      });
    } catch {
      /* map not ready */
    }
  }, [onViewport]);

  const fit = useCallback(() => {
    const map = mapRef.current;
    if (!map || !bounds) return;
    try {
      map.fitBounds(bounds, { padding: 48, duration: 0, maxZoom: 15 });
    } catch {
      /* map not ready */
    }
  }, [bounds]);

  const fitted = useRef<string | undefined | null>(null);
  useEffect(() => {
    if (!bounds || fitted.current === fitKey) return;
    fitted.current = fitKey;
    fit();
  }, [fitKey, bounds, fit]);

  // Follow the truck, throttled so the camera is not re-aimed every frame — but
  // with a trailing update, so the camera always ends on the latest position.
  const lastFollow = useRef(0);
  const followTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const followTarget = useRef<[number, number] | null>(null);
  const cancelFollow = useCallback(() => {
    if (followTimer.current !== null) clearTimeout(followTimer.current);
    followTimer.current = null;
  }, []);
  useEffect(() => {
    if (!follow || !position) {
      cancelFollow();
      return;
    }
    followTarget.current = [position.lon, position.lat];
    const aim = () => {
      followTimer.current = null;
      const map = mapRef.current;
      const target = followTarget.current;
      if (!map || !target) return;
      lastFollow.current = Date.now();
      try {
        map.easeTo({ center: target, duration: 500 });
      } catch {
        /* map not ready */
      }
    };
    const wait = FOLLOW_EVERY_MS - (Date.now() - lastFollow.current);
    if (wait <= 0) aim();
    else if (followTimer.current === null) followTimer.current = setTimeout(aim, wait);
  }, [follow, position, cancelFollow]);
  useEffect(() => cancelFollow, [cancelFollow]);

  const onLoad = (e: { target: MaplibreMap }) => {
    mapRef.current = e.target;
    fitted.current = fitKey;
    fit();
    report();
  };

  const toggleButton = (label: string, on: boolean, toggle: () => void) => (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={on}
      className={`${mapButton} ${on ? mapButtonOn : mapButtonOff}`}
    >
      {label}
    </button>
  );

  const basemapToggle = (
    <div
      // Above the expanded attribution on a phone (it spans the map's foot there).
      className="absolute bottom-12 sm:bottom-3 left-3 z-[1000] flex overflow-hidden rounded-lg shadow-lg bg-surface/95 backdrop-blur text-xs font-medium"
      role="group"
      aria-label="Basemap"
    >
      {BASEMAPS.map((b) => (
        <button
          key={b.id}
          type="button"
          onClick={() => setBasemap(b.id)}
          aria-pressed={basemap === b.id}
          className={`px-3 py-1.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 ${
            basemap === b.id ? mapButtonOn : 'text-slate-700 hover:bg-slate-100'
          }`}
        >
          {b.label}
        </button>
      ))}
    </div>
  );

  const hollow =
    position?.motion === 'before' || position?.motion === 'after' || (!!display && isHollowDisplay(display));
  // ST-00276: the colour at the clock — the display rules' (else the stage's).
  const assetKey: AssetStatusKey = (display?.base.token as AssetStatusKey | undefined) ?? stageKey(position?.stage);
  const assetName = assetKind === 'loader' ? 'Loader' : 'Truck';

  // ST-00270: a click on the asset opens its card — name, status and speed at the replay time.
  // The card follows the asset while the replay plays or is scrubbed (it always describes the
  // moment on the clock); Esc, its close button or a click on the map closes it.
  const [infoOpen, setInfoOpen] = useState(false);
  const infoTitleId = useId();
  const markerButtonRef = useRef<HTMLButtonElement | null>(null);
  const registration = replay.asset?.registration || registrationOf(replay.asset_key) || assetName;
  const closeInfo = useCallback((refocus = false) => {
    setInfoOpen(false);
    if (refocus) markerButtonRef.current?.focus();
  }, []);
  useEffect(() => {
    if (!infoOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeInfo(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [infoOpen, closeInfo]);
  const infoKey = assetKey;
  const infoMotion =
    position?.motion === 'moving'
      ? 'moving'
      : position?.motion === 'stationary'
        ? 'standing'
        : 'no position at this time';

  // The status keys of this replay's stages, in the scheme's order (the legend's track entries);
  // ST-00276: the keys the display rules gave the track, when it is coloured by them.
  const stageKeys = useMemo(() => {
    const present = displayBands && displayBands.length
      ? [...new Set(displayBands.map((b) => b.display.base.token as AssetStatusKey))]
      : [...new Set(replay.stage_bands.map((b) => stageKey(b.stage)))];
    // Generated from the active rules (map/assetVisualRules legendFromRules).
    return legendFromRules({}, { keys: present, overlayIds: [] })
      .map((e) => e.key)
      .filter((k): k is AssetStatusKey => !!k);
  }, [replay.stage_bands, displayBands]);
  const ownRules = (displayLegend?.entries || []).filter((e) => e.kind === 'rule');
  const marks = (displayLegend?.entries || []).filter((e) => e.kind === 'overlay' && e.mark && e.mark !== 'dashed');

  const legendItem = (key: string, swatch: ReactNode, label: string) => (
    <li key={key} className="inline-flex items-center gap-1.5">
      {swatch}
      {label}
    </li>
  );

  return (
    <div>
      <div className={className}>
        <LexiStarMap
          className="absolute inset-0"
          initialViewState={DEFAULT_VIEW}
          basemap={basemap}
          onBasemapChange={setBasemap}
          showBasemapToggle={false}
          navigationPosition="top-left"
          onLoad={onLoad}
          onMoveEnd={report}
          overlay={
            <>
              <div className="absolute top-3 right-3 z-[1000] flex gap-2">
                {zones.available && toggleButton('Zones', zones.on, zones.toggle)}
                {planned.corridor &&
                  toggleButton('Corridor', showCorridor, () => setShowCorridor((v) => !v))}
                {toggleButton('Speed', bySpeed, () => setBySpeed((v) => !v))}
                {toggleButton('Follow', follow, () => setFollow((v) => !v))}
                <button type="button" onClick={fit} className={`${mapButton} ${mapButtonOff}`}>
                  Fit
                </button>
              </div>
              {basemapToggle}
            </>
          }
        >
          {zones.layer}
          {showCorridor && planned.corridor && (
            <Source id="rp-corridor" type="geojson" data={planned.corridors}>
              <Layer
                id="rp-corridor-fill"
                type="fill"
                paint={{ 'fill-color': PLANNED, 'fill-opacity': 0.12 }}
              />
            </Source>
          )}
          {planned.routes.features.length > 0 && (
            <Source id="rp-planned" type="geojson" data={planned.routes}>
              <Layer
                id="rp-planned-line"
                type="line"
                filter={['!', ['get', 'replaced']]}
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{ 'line-color': PLANNED, 'line-width': 2.5 }}
              />
              <Layer
                id="rp-planned-replaced"
                type="line"
                filter={['get', 'replaced']}
                paint={{ 'line-color': '#94a3b8', 'line-width': 2, 'line-dasharray': [2, 2] }}
              />
            </Source>
          )}
          {tracks.base.features.length > 0 && (
            <Source id="rp-track" type="geojson" data={tracks.base}>
              <Layer
                id="rp-track-line"
                type="line"
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{ 'line-color': '#64748b', 'line-width': 4.5, 'line-opacity': 0.7 }}
              />
            </Source>
          )}
          {speed && (
            <Source id="rp-speed" type="geojson" data={speed.line} lineMetrics>
              <Layer
                id="rp-speed-line"
                type="line"
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{
                  'line-color': speed.color,
                  'line-width': 4.5,
                  ...(speed.gradient ? { 'line-gradient': speed.gradient } : {}),
                }}
              />
            </Source>
          )}
          {!speed && tracks.stages.features.length > 0 && (
            <Source id="rp-stages" type="geojson" data={tracks.stages}>
              <Layer
                id="rp-stage-line"
                type="line"
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{ 'line-color': ['get', 'color'], 'line-width': 4.5 }}
              />
            </Source>
          )}
          {deviations.features.length > 0 && (
            <Source id="rp-deviations" type="geojson" data={deviations}>
              <Layer
                id="rp-deviation-unapproved"
                type="line"
                filter={['!', ['get', 'approved']]}
                layout={{ 'line-cap': 'round' }}
                paint={{ 'line-color': DEVIATION, 'line-width': 6 }}
              />
              <Layer
                id="rp-deviation-approved"
                type="line"
                filter={['get', 'approved']}
                paint={{ 'line-color': DEVIATION, 'line-width': 5, 'line-dasharray': [2, 1.5] }}
              />
            </Source>
          )}
          {stopsFC.features.length > 0 && (
            <Source id="rp-stops" type="geojson" data={stopsFC}>
              <Layer
                id="rp-stops-circle"
                type="circle"
                paint={{
                  'circle-radius': 4.5,
                  'circle-color': MARK_PAPER,
                  'circle-stroke-color': '#334155',
                  'circle-stroke-width': 2,
                }}
              />
            </Source>
          )}
          {/* The track's first and last fix (ST-00318) */}
          {trackStart && (
            <Marker longitude={trackStart.lon} latitude={trackStart.lat}>
              <span
                data-testid="replay-track-start"
                title={`Start of the track · ${clockTime(trackStart.t)}`}
                className="block"
              >
                <EndMark letter="A" color={TRACK_START} />
              </span>
            </Marker>
          )}
          {trackEnd && (
            <Marker longitude={trackEnd.lon} latitude={trackEnd.lat}>
              <span
                data-testid="replay-track-end"
                title={`End of the track · ${clockTime(trackEnd.t)}`}
                className="block"
              >
                <EndMark letter="B" color={TRACK_END} />
              </span>
            </Marker>
          )}
          {events.map((e) => {
            const selected = e.id === selectedEventId;
            return (
              <Marker
                key={`ev-${e.id || e.t}`}
                longitude={e.lon}
                latitude={e.lat}
                onClick={(ev) => {
                  ev?.originalEvent?.stopPropagation?.();
                  onSelectEvent?.(e);
                }}
              >
                <span
                  title={`${eventTypeLabel(e.type)} · ${new Date(e.t).toLocaleTimeString('en-GB', { timeZone: 'Africa/Johannesburg', hour12: false })}`}
                  className={`block rotate-45 cursor-pointer border-2 ${selected ? 'w-3.5 h-3.5' : 'w-2.5 h-2.5'}`}
                  style={{ borderColor: MARK_INK, background: selected ? MARK_INK : MARK_PAPER }}
                />
              </Marker>
            );
          })}
          {position && (
            <Marker longitude={position.lon} latitude={position.lat} style={{ zIndex: 3 }}>
              <button
                ref={markerButtonRef}
                type="button"
                title={`${registration}: show its status and speed`}
                aria-label={`${registration}: show its status and speed at the replay time`}
                aria-expanded={infoOpen}
                aria-haspopup="dialog"
                onClick={(ev) => {
                  ev.stopPropagation();
                  setInfoOpen((o) => !o);
                }}
                data-testid="replay-asset"
                data-motion={position.motion}
                className="block p-0 border-0 bg-transparent cursor-pointer rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                style={{ width: ASSET_ICON_PX, height: ASSET_ICON_PX }}
              >
                <MarkerHalo overlays={display?.overlays} size={ASSET_ICON_PX}>
                  <AssetMarkerIcon
                    kind={assetKind}
                    status={assetKey}
                    heading={position.heading}
                    moving={position.motion === 'moving'}
                    hollow={hollow}
                    size={ASSET_ICON_PX}
                    label={`${assetName} at the replay time`}
                  />
                </MarkerHalo>
              </button>
            </Marker>
          )}
          {position && infoOpen && (
            <Popup
              longitude={position.lon}
              latitude={position.lat}
              anchor="bottom"
              offset={ASSET_ICON_PX / 2 + 2}
              closeOnClick
              onClose={() => closeInfo(false)}
              className={ASSET_INFO_POPUP_CLASS}
              maxWidth="280px"
            >
              <div role="dialog" aria-labelledby={infoTitleId} data-testid="replay-asset-info">
                <AssetInfoCard
                  titleId={infoTitleId}
                  name={registration}
                  subtitle={replay.asset?.fleet_no || assetName}
                  statusKey={infoKey}
                  statusLabel={display?.base.label}
                  motionText={infoMotion}
                  speedKmh={hollow ? null : position.speed_kmh}
                  when={`At ${clockTime(position.t)}`}
                >
                  <DisplayMarks overlays={display?.overlays} className="mt-1.5" />
                </AssetInfoCard>
              </div>
            </Popup>
          )}
        </LexiStarMap>
      </div>
      <ul
        className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 px-1 text-[11px] text-slate-600"
        aria-label="Map legend"
      >
        {legendItem(
          'asset',
          <AssetMarkerIcon
            kind={assetKind}
            status="loaded"
            heading={90}
            size={20}
            style={{ filter: 'none' }}
          />,
          `${assetName} at the replay time`
        )}
        {legendItem(
          'planned',
          <span className="inline-block w-5 h-0.5" style={{ background: PLANNED }} />,
          'Planned route'
        )}
        {planned.replanned &&
          legendItem(
            'replaced',
            <span className="inline-block w-5 h-0" style={{ borderTop: '2px dashed #94a3b8' }} />,
            'Replaced plan'
          )}
        {planned.corridor &&
          legendItem(
            'corridor',
            <span
              className="inline-block w-5 h-2.5 rounded-sm"
              style={{ background: `${PLANNED}33`, boxShadow: `inset 0 0 0 1px ${PLANNED}66` }}
            />,
            'Corridor'
          )}
        {legendItem(
          'ends',
          <span className="inline-flex gap-0.5">
            <EndMark letter="A" color={TRACK_START} px={14} />
            <EndMark letter="B" color={TRACK_END} px={14} />
          </span>,
          'Start and end of the track'
        )}
        {/* ST-00318: the track by speed — its bands (the truck keeps its stage / rule colour). */}
        {speed &&
          SPEED_BANDS.map((b) =>
            legendItem(
              `speed-${b.label}`,
              <span className="inline-block w-5 h-1 rounded" style={{ background: b.color }} />,
              b.label
            )
          )}
        {/* Track by stage: one entry per status key this replay's stages use (the same keys
            and colours as the Status Board and Live View, map/assetStatus). */}
        {stageKeys.map((k) =>
          legendItem(
            `stage-${k}`,
            <span
              className={`inline-block w-5 h-1 rounded ${ASSET_STATUS_CLASSES[k].dot}`}
              data-legend-key={k}
            />,
            ASSET_STATUS[k].label
          )
        )}
        {/* ST-00276: the organisation's own colour rules and marks, and what Replay cannot show. */}
        {ownRules.map((e) =>
          legendItem(
            `rule-${e.id}`,
            <span
              className={`inline-block w-5 h-1 rounded ${ASSET_STATUS_CLASSES[e.token as AssetStatusKey].dot}`}
              data-legend-rule={e.id}
            />,
            e.label
          )
        )}
        {marks.map((e) =>
          legendItem(
            `mark-${e.id}`,
            <span className="inline-flex w-5 justify-center" data-legend-mark={e.id}>
              <MarkGlyph mark={e.mark!} token={e.token} />
            </span>,
            e.label
          )
        )}
        {legendItem(
          'unapproved',
          <span className="inline-block w-5 h-1" style={{ background: DEVIATION }} />,
          'Unapproved deviation'
        )}
        {legendItem(
          'approved',
          <span
            className="inline-block w-5 h-0"
            style={{ borderTop: `3px dashed ${DEVIATION}` }}
          />,
          'Approved detour'
        )}
        {legendItem(
          'event',
          <span
            className="inline-block w-2 h-2 rotate-45 border-2"
            style={{ borderColor: MARK_INK, background: MARK_PAPER }}
          />,
          'Event'
        )}
        {legendItem(
          'stop',
          <span
            className="inline-block w-2.5 h-2.5 rounded-full border-2"
            style={{ borderColor: '#334155', background: MARK_PAPER }}
          />,
          'Stop / idle'
        )}
        {zones.legend}
      </ul>
      {displayLegend && displayLegend.notShown.length > 0 && (
        <p className="mt-1 px-1 text-[11px] text-slate-500" data-testid="replay-not-historical">
          Your map colours also use {displayLegend.notShown.join(', ')}. Replay does not keep those for
          past moments, so the rules that need them cannot show here.
        </p>
      )}
    </div>
  );
}
